import moment from 'moment';

// test: yarn test utils/__tests__/periodicBookingHelper.spec.js

/**
 * Calculate next N occurrences
 * @param {Object} periodicBooking - The periodic booking object with timing_config, first_booking_date, last_booking_date, start_at, end_at
 * @param {Number} count - Optional. Number of occurrences to calculate. If not provided, uses last_booking_date
 * @returns {Array} Array of objects with { start, end } formatted as 'YYYY-MM-DD HH:mm:ss' strings
 */
export function calculateFutureOccurrences(periodicBooking, count = null) {
  const occurrences = [];
  const { timing_config, first_booking_date, last_booking_date, start_at, end_at } = periodicBooking;
  const { period_type, period_interval, period_day_of_week, period_day_of_month } = timing_config;

  // Validate that either count or last_booking_date is provided
  if (count === null && !last_booking_date) {
    console.warn('Either count or last_booking_date must be provided to prevent infinite loops', periodicBooking);
    const fallbackDate = moment(start_at || first_booking_date).format('YYYY-MM-DD HH:mm:ss');
    return [{ start: fallbackDate, end: fallbackDate }];
  }

  // period_interval <= 0 should not be allowed
  if (!period_interval || period_interval <= 0) {
    console.warn('period_interval must be greater than 0', periodicBooking);
    const fallbackDate = moment(start_at || first_booking_date).format('YYYY-MM-DD HH:mm:ss');
    return [{ start: fallbackDate, end: fallbackDate }];
  }

  // Use start_at if provided, otherwise use first_booking_date
  const startDateTime = start_at ? moment(start_at) : moment(first_booking_date);
  const endDateTime = end_at ? moment(end_at) : null;

  // Calculate duration between start and end if both are provided
  const duration = endDateTime ? endDateTime.diff(startDateTime) : null;

  let currentDate = moment(first_booking_date);
  const lastDate = last_booking_date ? moment(last_booking_date) : moment().add(1, 'year');

  // Determine if we should limit by count or by date
  const useCountLimit = count !== null;

  // For weekly recurrence, find the first occurrence on the specified day of week
  if (period_type === 'weekly' && period_day_of_week !== null && period_day_of_week !== undefined) {
    // Adjust to the first occurrence of the specified day of week
    const targetDay = parseInt(period_day_of_week);
    const currentDay = currentDate.day();
    const daysToAdd = (targetDay - currentDay + 7) % 7;
    if (daysToAdd > 0) {
      currentDate.add(daysToAdd, 'days');
    }
  }

  // For monthly recurrence, find the first occurrence on the specified day of month
  if (period_type === 'monthly' && period_day_of_month !== null && period_day_of_month !== undefined) {
    const targetDay = parseInt(period_day_of_month);
    const currentDay = currentDate.date();

    if (currentDay > targetDay) {
      // Move to next month and set to target day
      currentDate.add(1, 'month').date(targetDay);
    } else if (currentDay < targetDay) {
      // Set to target day in current month
      currentDate.date(targetDay);
    }
  }

  // If the adjusted date is beyond last_booking_date, use first_booking_date as the only occurrence
  if (currentDate.isAfter(lastDate)) {
    currentDate = moment(first_booking_date);
  }

  while (currentDate.isSameOrBefore(lastDate)) {
    // Break if we've reached the count limit (when count is provided)
    if (useCountLimit && occurrences.length >= count) {
      break;
    }
    // Create occurrence start time by combining current date with start time
    const occurrenceStart = currentDate.clone()
      .hour(startDateTime.hour())
      .minute(startDateTime.minute())
      .second(startDateTime.second());

    // Create occurrence end time if duration exists
    const occurrenceEnd = duration !== null ? occurrenceStart.clone().add(duration) : null;

    occurrences.push({
      start: occurrenceStart.format('YYYY-MM-DD HH:mm:ss'),
      end: occurrenceEnd ? occurrenceEnd.format('YYYY-MM-DD HH:mm:ss') : null
    });

    // Increment based on recurrence type
    switch (period_type) {
      case 'daily':
        currentDate.add(period_interval, 'days');
        break;
      case 'weekly':
        currentDate.add(period_interval, 'weeks');
        break;
      case 'monthly':
        currentDate.add(period_interval, 'months');
        // Ensure we stay on the correct day of month
        if (period_day_of_month !== null && period_day_of_month !== undefined) {
          currentDate.date(Math.min(period_day_of_month, currentDate.daysInMonth()));
        }
        break;
    }
  }

  return occurrences;
}

/**
 * Calculate preview dates for creating a new periodic booking
 * @param {Object} periodicBooking - Object with timing_config, last_booking_date, start_at, end_at
 * @param {Number} limit - Optional. Number of occurrences to return
 * @returns {Array} Array of occurrence objects
 */
export function previewDatesForCreating(periodicBooking, limit = 5) {
  const { timing_config, start_at } = periodicBooking;
  if (!timing_config?.period_type || !start_at) {
    return [];
  }

  const firstBookingDate = moment(start_at).format('YYYY-MM-DD');

  return calculateFutureOccurrences({
    ...periodicBooking,
    first_booking_date: firstBookingDate,
  }, limit);
}

/**
 * Calculate preview dates for updating an existing periodic booking
 * @param {Object} periodicBooking - Object with timing_config, first_booking_date, last_booking_date, start_at, end_at
 * @param {String} scope - Mutation scope ('this', 'future', 'all')
 * @param {Number} limit - Optional. Number of occurrences to return
 * @returns {Array} Array of occurrence objects
 */
export function previewDatesForExisting(periodicBooking, scope, limit = 5) {
  const { timing_config, first_booking_date, last_booking_date, start_at, end_at } = periodicBooking;
  if (!timing_config?.period_type || !start_at) {
    return [];
  }

  const now = moment();
  const today = moment().startOf('day');
  let firstBookingDateCalc, lastBookingDateCalc;

  if (scope === 'this') {
    return [
      {
        start: moment(start_at).format('YYYY-MM-DD HH:mm:ss'),
        end: moment(end_at).format('YYYY-MM-DD HH:mm:ss')
      }
    ];
  } else if (scope === 'future') {
    firstBookingDateCalc = moment(start_at).format('YYYY-MM-DD');
    lastBookingDateCalc = last_booking_date;
  } else if (scope === 'all') {
    const originalFirstDate = moment(first_booking_date);

    if (originalFirstDate.isAfter(today)) {
      firstBookingDateCalc = first_booking_date;
    } else {
      // Find the next occurrence after today using CURRENT settings
      const tempOccurrences = calculateFutureOccurrences(periodicBooking, last_booking_date ? null : limit);

      const nextOccurrence = tempOccurrences.find(occ =>
        moment(occ.start).isAfter(today)
      );

      firstBookingDateCalc = nextOccurrence
        ? moment(nextOccurrence.start).format('YYYY-MM-DD')
        : today.format('YYYY-MM-DD');
    }
    lastBookingDateCalc = last_booking_date;
  }

  const allOccurrences = calculateFutureOccurrences({
    ...periodicBooking,
    first_booking_date: firstBookingDateCalc,
    last_booking_date: lastBookingDateCalc,
  }, limit);

  if (scope === 'all') {
    return allOccurrences.filter(occ =>
      moment(occ.start).isAfter(now)
    ).slice(0, limit);
  }

  return allOccurrences;
}
